import { useState, useEffect } from "react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, MapPin, User, CreditCard, Plus, RefreshCw } from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useBookings } from "@/hooks/useBookings";
import { useFavorites } from "@/hooks/useFavorites";
import { useUserMemberships } from "@/hooks/useUserMemberships";
import { useAuth } from "@/hooks/useAuth";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useIsMobile } from "@/hooks/use-mobile";
import BookingCard from "@/components/dashboard/BookingCard";
import FavoriteCard from "@/components/dashboard/FavoriteCard";
import StatsCard from "@/components/dashboard/StatsCard";
import NoBookingsState from "@/components/dashboard/NoBookingsState";
import UserMembershipCard from "@/components/dashboard/UserMembershipCard";
import SearchModal from "@/components/SearchModal";
import { formatFirstNameForWelcome } from "@/utils/userNameUtils";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import type { UserMembership } from "@/hooks/useUserMemberships";

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [showExpiredMemberships, setShowExpiredMemberships] = useState(false);
  
  // Check URL parameter for default tab selection
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'memberships' ? 'memberships' : tabParam === 'favorites' ? 'favorites' : 'bookings');
  
  const { data: bookings = [], isLoading, error, refetch } = useBookings();
  const { favorites, isLoading: favoritesLoading } = useFavorites();
  const { data: allUserMemberships = [], isLoading: membershipLoading } = useUserMemberships();
  const { user, loading: authLoading, hasCompleteProfile } = useAuth();
  const { data: providerAccess, isLoading: providerAccessLoading } = useProviderAccess();
  const isMobile = useIsMobile();

  // Fetch user subscriptions
  const { data: userSubscriptions = [] } = useQuery({
    queryKey: ['user-subscriptions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          membership:memberships(
            id,
            name,
            description,
            membership_type,
            session_count,
            price,
            currency,
            provider_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching subscriptions:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!user,
  });

  // Handle authentication and profile completion redirects
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      console.log("No user found on dashboard, redirecting to auth");
      navigate("/auth", { replace: true });
      return;
    }

    if (!hasCompleteProfile) {
      console.log("User has incomplete profile, redirecting to profile completion");
      navigate("/profile-completion", { replace: true });
      return;
    }
  }, [user, authLoading, hasCompleteProfile, navigate]);


  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render dashboard if no user or incomplete profile
  if (!user || !hasCompleteProfile) {
    return null;
  }

  // Use only the first name for welcome message
  const welcomeName = formatFirstNameForWelcome(user?.user_metadata?.first_name);

  // Filter bookings by status
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentTime = new Date();

  const upcomingBookings = bookings
    .filter(booking => {
      const bookingDate = new Date(booking.booking_date);
      bookingDate.setHours(0, 0, 0, 0);
      const sessionEnd = new Date(`${booking.booking_date}T${booking.end_time}`);
      
      return (bookingDate >= today || currentTime < sessionEnd) && 
             booking.status !== 'Completed' && 
             booking.status !== 'Cancelled';
    })
    .sort((a, b) => {
      const dateComparison = new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime();
      if (dateComparison !== 0) return dateComparison;
      return b.start_time.localeCompare(a.start_time);
    });

  const pastBookings = bookings
    .filter(booking => {
      const bookingDate = new Date(booking.booking_date);
      bookingDate.setHours(0, 0, 0, 0);
      const sessionEnd = new Date(`${booking.booking_date}T${booking.end_time}`);
      
      return (bookingDate < today && currentTime >= sessionEnd) || 
             booking.status === 'Completed' || 
             booking.status === 'Cancelled';
    })
    .sort((a, b) => {
      const dateComparison = new Date(b.booking_date).getTime() - new Date(a.booking_date).getTime();
      if (dateComparison !== 0) return dateComparison;
      return b.start_time.localeCompare(a.start_time);
    });

  // Transform favorites data for display
  const displayFavorites = favorites.map(fav => ({
    id: fav.provider_id,
    name: fav.providers?.name || 'Unknown Provider',
    category: fav.providers?.specialties?.join(', ') || 'Wellness Center',
    image: fav.providers?.image_url,
    slug: fav.providers?.slug
  }));

  // Get favorite provider slug for NoBookingsState
  const favoriteProviderSlug = favorites.length > 0 ? favorites[0]?.providers?.slug : null;

  // Helper function to check if subscription is active
  const isSubscriptionActive = (subscription: any) => {
    if (subscription.status === 'active') return true;
    if (subscription.current_period_end) {
      const now = new Date();
      const periodEnd = new Date(subscription.current_period_end);
      return now <= periodEnd && subscription.status !== 'canceled';
    }
    return false;
  };

  // Convert subscriptions to membership-like objects
  const subscriptionMemberships: UserMembership[] = userSubscriptions.map(subscription => ({
    id: `subscription-${subscription.id}`,
    user_id: subscription.user_id,
    membership_id: subscription.membership_id,
    sessions_remaining: null,
    sessions_used: 0,
    purchase_date: subscription.created_at,
    expires_at: subscription.current_period_end,
    is_active: isSubscriptionActive(subscription),
    membership: subscription.membership,
    isSubscription: true,
    subscriptionStatus: subscription.status
  }));

  // Combine regular memberships with subscription memberships
  const allMemberships = [
    ...allUserMemberships.filter(membership => membership.membership),
    ...subscriptionMemberships.filter(sub => sub.membership)
  ];

  // Filter memberships based on active status
  const activeMemberships = allMemberships.filter(membership => {
    if (!membership.membership) return false;
    if (!membership.is_active) return false;
    
    if (membership.isSubscription) {
      return isSubscriptionActive(membership);
    }
    
    if (membership.expires_at) {
      const now = new Date();
      const expiresAt = new Date(membership.expires_at);
      if (now > expiresAt) return false;
    }
    
    if (membership.membership.membership_type === 'pack_of_sessions' && 
        membership.sessions_remaining === 0) {
      return false;
    }
    
    return true;
  });

  const expiredMemberships = allMemberships.filter(membership => {
    if (!membership.membership) return false;
    if (!membership.is_active) return true;
    
    if (membership.isSubscription) {
      return !isSubscriptionActive(membership);
    }
    
    if (membership.expires_at) {
      const now = new Date();
      const expiresAt = new Date(membership.expires_at);
      if (now > expiresAt) return true;
    }
    
    if (membership.membership.membership_type === 'pack_of_sessions' && 
        membership.sessions_remaining === 0) {
      return true;
    }
    
    return false;
  });

  const displayedMemberships = showExpiredMemberships ? expiredMemberships : activeMemberships;

  const handleRefreshBookings = () => {
    console.log("Manually refreshing bookings...");
    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SearchModal open={searchModalOpen} onOpenChange={setSearchModalOpen} />
      <DashboardHeader />
      
      <main className="max-w-4xl mx-auto px-4 py-6 flex-1 w-full">
        {/* Header with welcome and quick stats */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, {welcomeName}</h1>
              <p className="text-gray-500 text-sm hidden sm:block">Manage your bookings and memberships</p>
            </div>
            <Button 
              onClick={() => {
                if (favoriteProviderSlug) {
                  navigate(`/provider/${favoriteProviderSlug}`);
                } else {
                  setSearchModalOpen(true);
                }
              }}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{favoriteProviderSlug ? "Book Now" : "New Booking"}</span>
            </Button>
          </div>
          
          {/* Quick Stats Bar */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span className="font-medium text-gray-900">{upcomingBookings.length}</span>
              <span>upcoming</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-4 w-4" />
              <span className="font-medium text-gray-900">{activeMemberships.length}</span>
              <span>active memberships</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefreshBookings}
              className="ml-auto text-gray-500 hover:text-gray-700 hidden sm:flex"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start bg-transparent border-b border-gray-200 rounded-none p-0 h-auto gap-6">
            <TabsTrigger 
              value="bookings" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium text-gray-600 data-[state=active]:text-gray-900"
            >
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="favorites" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium text-gray-600 data-[state=active]:text-gray-900"
            >
              Favourites
            </TabsTrigger>
            <TabsTrigger 
              value="memberships" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent bg-transparent px-0 pb-3 pt-0 font-medium text-gray-600 data-[state=active]:text-gray-900"
            >
              Memberships
            </TabsTrigger>
          </TabsList>
          
          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mt-6">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your bookings...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">Error loading bookings. Please try refreshing.</p>
                <Button onClick={handleRefreshBookings} variant="outline">
                  Try Again
                </Button>
              </div>
            ) : (
              <Tabs defaultValue="upcoming" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
                  <TabsTrigger value="upcoming" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                    Upcoming ({upcomingBookings.length})
                  </TabsTrigger>
                  <TabsTrigger value="past" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                    Past ({pastBookings.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="upcoming" className="mt-6">
                  {upcomingBookings.length > 0 ? (
                    <div className="space-y-4">
                      {upcomingBookings.map((booking) => (
                        <BookingCard key={booking.id} booking={booking} showActions={true} />
                      ))}
                    </div>
                  ) : (
                    <NoBookingsState onSearchProviders={() => setSearchModalOpen(true)} favoriteProviderSlug={favoriteProviderSlug} />
                  )}
                </TabsContent>
                
                <TabsContent value="past" className="mt-6">
                  {pastBookings.length > 0 ? (
                    <div className="space-y-4">
                      {pastBookings.map((booking) => (
                        <BookingCard key={booking.id} booking={booking} showActions={true} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                      <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No past bookings yet.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </TabsContent>
          
          {/* Favorites Tab */}
          <TabsContent value="favorites" className="mt-6">
            {favoritesLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your favourites...</p>
              </div>
            ) : displayFavorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayFavorites.map((favorite) => (
                  <FavoriteCard key={favorite.id} favorite={favorite} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 mb-4">You haven't added any favourites yet.</p>
                <Button 
                  variant="outline"
                  onClick={() => setSearchModalOpen(true)}
                >
                  Find Providers
                </Button>
              </div>
            )}
          </TabsContent>
          
          {/* Memberships Tab */}
          <TabsContent value="memberships" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {showExpiredMemberships ? 'Showing expired memberships' : 'Showing active memberships'}
              </p>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowExpiredMemberships(!showExpiredMemberships)}
                className="text-gray-600 hover:text-gray-900"
              >
                {showExpiredMemberships ? 'Show Active' : 'Show Expired'}
              </Button>
            </div>
            
            {membershipLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your memberships...</p>
              </div>
            ) : displayedMemberships.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayedMemberships.map((membership) => (
                  <UserMembershipCard key={membership.id} membership={membership} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                {showExpiredMemberships ? (
                  <p className="text-gray-600">You don't have any expired memberships.</p>
                ) : (
                  <>
                    <p className="text-gray-600 mb-4">You don't have any active memberships yet.</p>
                    <Button 
                      variant="outline"
                      onClick={() => setSearchModalOpen(true)}
                    >
                      Browse Providers
                    </Button>
                  </>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-auto">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              &copy; 2025 Wellsuites. All rights reserved.
            </p>
            <nav className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
              <Link to="/support" className="hover:text-gray-900 transition-colors">Support</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;